/**
 * The Amoreg mark's geometry, and the small two-colour mark a tool row wears
 * when the work is Amoreg's own.
 *
 * @module components/AmoregBrandMark
 */
import { cn } from "../lib/utils";

/** The mark's two shapes: the body, and the short "leg" at its foot. */
export const AMOREG_MARK_LEG_PATH =
  "M71.8086 71.6095C72.5016 72.3655 72.8482 72.7436 72.8637 73.0655C72.8772 73.3451 72.7585 73.6148 72.5433 73.7939C72.2955 74 71.7827 74 70.7571 74H56.3953C56.1641 74 56.0485 74 55.9406 73.9726C55.8448 73.9483 55.7538 73.9082 55.6712 73.8541C55.578 73.793 55.4999 73.7078 55.3437 73.5374L50.6246 68.3893C50.1847 67.9095 49.9648 67.6696 49.9159 67.4259C49.8719 67.2066 49.9069 66.9954 50.0194 66.802C50.1444 66.5872 50.4401 66.4256 51.0316 66.1025C54.0294 64.4647 56.7992 62.4606 59.2804 60.1494C59.6742 59.7826 59.8711 59.5992 60.0781 59.5405C60.2644 59.4876 60.4498 59.4949 60.6314 59.5623C60.8331 59.6371 61.0089 59.8289 61.3606 60.2125L71.8086 71.6095Z";
export const AMOREG_MARK_BODY_PATH =
  "M31.5761 0C48.2607 0 61.7862 13.5536 61.7862 30.2727C61.7862 46.9919 48.2607 60.5455 31.5761 60.5455H14.9605C14.4612 60.5455 14.2116 60.5455 14.0208 60.6426C13.8531 60.7281 13.7167 60.8645 13.6312 61.0323C13.534 61.223 13.534 61.4726 13.534 61.972V72.5735C13.534 73.0728 13.534 73.3225 13.4369 73.5132C13.3514 73.681 13.215 73.8173 13.0472 73.9028C12.8565 74 12.6069 74 12.1075 74H1.53386C1.03454 74 0.784875 74 0.594158 73.9028C0.426399 73.8173 0.290007 73.681 0.204529 73.5132C0.107354 73.3225 0.107354 73.0728 0.107354 72.5735V48.5174C0.107354 48.0181 0.107354 47.7684 0.204529 47.5777C0.290007 47.41 0.426399 47.2736 0.594158 47.1881C0.784875 47.0909 1.03454 47.0909 1.53386 47.0909H27.8192C28.8496 47.0909 29.3648 47.0909 29.6128 46.8841C29.8282 46.7045 29.9465 46.4341 29.9323 46.154C29.9158 45.8315 29.5662 45.4531 28.867 44.6963L0.378779 13.8645C0.238764 13.713 0.168756 13.6372 0.118796 13.5504C0.0744913 13.4734 0.0419428 13.3902 0.0222293 13.3036C0 13.2059 0 13.1027 0 12.8964V1.4265C0 0.927181 0 0.67752 0.0971749 0.486804C0.182652 0.319045 0.319045 0.182652 0.486804 0.0971749C0.67752 0 0.927182 0 1.42651 0H31.5761ZM28.0575 23.9448C28.0698 23.9583 28.0873 23.9659 28.1055 23.9659C28.1238 23.9659 28.1413 23.9736 28.1536 23.9871L36.9314 33.5624C36.9364 33.5678 36.9371 33.5758 36.9328 33.5817C36.9286 33.5877 36.9293 33.5958 36.9343 33.6012L43.0618 40.2673C43.4282 40.6659 43.6114 40.8652 43.8428 40.9361C44.0348 40.9949 44.2747 40.9815 44.4589 40.9016C44.6809 40.8053 44.8283 40.6037 45.1231 40.2005C47.1575 37.4181 48.3595 33.9864 48.3595 30.2727C48.3595 20.9843 40.8453 13.4545 31.5761 13.4545H21.6632C20.6359 13.4545 20.1222 13.4545 19.8743 13.6609C19.6591 13.8401 19.5405 14.1101 19.5543 14.3899C19.5701 14.712 19.9177 15.0902 20.613 15.8465L28.0575 23.9448Z";

/**
 * The Amoreg mark at tool-icon size: the body in the current text colour, so
 * it takes the row's ink in any theme, and the leg in the brand red. The
 * view box pads the mark to the optical size of the 24-unit line icons it
 * sits beside. `dimmed` fades the body the way those icons are faded; the
 * leg keeps its full colour.
 */
export function AmoregToolMark({
  className,
  dimmed = false,
}: {
  className?: string;
  dimmed?: boolean;
}) {
  return (
    <svg
      aria-hidden
      viewBox="-9 -8.5 91 91"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(className, "opacity-100")}
      data-amoreg-mark=""
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d={AMOREG_MARK_BODY_PATH}
        fill="currentColor"
        className={dimmed ? "opacity-70" : undefined}
      />
      <path d={AMOREG_MARK_LEG_PATH} fill="var(--brand-accent, #de5656)" />
    </svg>
  );
}
